import { Injectable } from '@angular/core';
import { FilterStoreService } from './store/filter-store.service';
import { AggregateRequestStoreService } from './store/aggregate-request-store.service';
import { LocalStorageService } from '../_services/local-storage.service';
import { FilterDataHandlerService } from '../_services/filter-data-handler.service';
import { AggReqObjectHandlerService } from '../_services/agg-req-object-handler.service';
import { TalentInsightsReqHandlerService } from '../_services/talent-insights-req-handler.service';
import { ActivatedRoute, Router } from '@angular/router';
import { ExtraInfo } from '../person-search-filter/ExtraInfo';
import { FilterParams } from '../common-interfaces-classes/FilterParams';
import { CallStage } from '../enums/CallStage';
import { GeneralConstants } from '../constants/GeneralConstants';
import * as _ from 'underscore';
import { AggregationRequest } from '../common-interfaces-classes/AggregationRequest';
import { filter, forkJoin, Observable, Subject } from 'rxjs';
import { PersonSearchService } from '../_services/person-search.service';
import { LoaderEventsService } from '../_services/loader-events.service';
import { LoaderType } from '../enums/LoaderType';
import { PersonInsightsData } from './hire-insights/PersonInsightsData';
import { FusionChartsService } from '../_services/fusion-charts.service';
import { AggregationService } from '../_services/aggregation.service';
import { JdDataService } from '../jd-data.service';

type StatusMap = {
  jobTitleStatus: string;
  companyStatus: string;
  industryStatus: string;
  yearsOfExperienceType: string;
};

export interface ExportPayload {
  type?: string
  tab?: 'overview'
  totalresultcount?: number
  data?: any
  aiData?: string
  aiRequestFilters?: any
  aiRequestPayload?: any[]
  [key: string]: any
}

/**
 * Data structure of each request object for person-search insights export
 */
export interface ExportRequest {
  type: string
  action?: string
  data: {
    currentTab: string
  }
}
@Injectable({
  providedIn: 'root',
})
export class RequestServiceService {

  constructor(
    private filterStore: FilterStoreService,
    private aggregateRequestStore: AggregateRequestStoreService,
    private localStorageService: LocalStorageService,
    private filterDataHandlerService: FilterDataHandlerService,
    private aggReqObjHandler: AggReqObjectHandlerService,
    private talentInsightsReqHandlerService: TalentInsightsReqHandlerService,
    private activatedRoute: ActivatedRoute,
    private personSearchService: PersonSearchService,
    private loaderEventsService: LoaderEventsService,
    private fusionChartService: FusionChartsService,
    private router: Router,
    private route: ActivatedRoute,
    private aggService: AggregationService,
   private jdDataService: JdDataService,


  ) { }

  // ── Geo IP (same as aggregation.service.ts) ────────────────────────────
  get geoIpLocation() {
    const location = JSON.parse(this.localStorageService.getItem('userGeoIpLocation'));
    const country = location?.country?.toLowerCase();
    const city = location?.city?.toLowerCase();
    const region = location?.region?.toLowerCase();
    const continent = location?.continent?.toLowerCase();
    return {
      location: `${city || ''}${city && (region || country) ? ', ' : ''}${region || ''}${region && country ? ', ' : ''}${country || ''}`,
      continent
    };
  }

  // ── Main method (replaces getRequestWithFilters, no JD/SC/AI/NL) ───────
  getHireRequestWithFilters(includeFilters: boolean = true) {
    const request = this.aggregateRequestStore.getAggregateRequest();
    const jdFilterData = this.jdDataService.jdDataFromSession
    request.companyids = [];
      if (!includeFilters) {
    return request;
  }

    const filterData = this.filterStore.getFilters();
    const statusMap = this.getStatusMap(filterData);

    // ── Apply core filters ─────────────────────────────────────────────
    this.applyFilters(request, filterData, statusMap);

    // ── Profile highlights, position hierarchy ─────────────────────────
    request.profilehighlights = filterData.profilehighlights;
    request.positionhierarchies = filterData.positionhierarchy;

    // ── History ────────────────────────────────────────────────────────
    request.talentdiscoveryhistoryid = filterData.talentdiscoveryhistoryid;
    request.savehistory = filterData.savehistory;




    request.locationcontinents = filterData.locationcontinents;

    // ── Locations (NO JD fallback, NO AI sourcing, NO SC checks) ──────
    if (filterData.locationSelectedList?.length) {
      request.locations = filterData.locationSelectedList
        .filter(l => l.isExcluded === false)
        .map(l => l.key);
    } else {
      if (
        this.geoIpLocation &&
        request.continentstobeincluded?.length &&
        this.geoIpLocation.continent === request.continentstobeincluded[0]
      ) {
        request.locations = [`${this.geoIpLocation.location}.iplocation`];
      }
      request.locations = this.route.snapshot.queryParams['redirectSource'] != 'company-insights' ? [this.localStorageService.getItem('defaultContinent')] : [];

    }
    request.continentstobeincluded = filterData?.continentstobeincluded?.length ? filterData?.continentstobeincluded : [this.localStorageService.getItem('defaultContinent')];
    if(this.route.snapshot.queryParams['redirectSource'] == 'company-insights') {
      request.continentstobeincluded = [];
    }

    // ── Mandatory & Insights filters ───────────────────────────────────
    this.applyMandatoryFilters(request, filterData);
  this.applyInsightsFilters(request, filterData);

    // ── Redirect / listing page cleanup ───────────────────────────────
    if (
      (this.filterDataHandlerService.isRedirectFromTalentInsightsPage ||
        this.filterDataHandlerService.isReportDetailsPage ||
        this.filterDataHandlerService.isRedirectFromCompanyInsightsPage) &&
      !!request.calledfrom
    ) {
      delete request.calledfrom;
    }

    this.filterDataHandlerService.isPersonSearchListingPage && (request.needcuratedtalentpooltags = true);

    // Quick search
    const searchType = this.activatedRoute.snapshot.queryParams['type'];
    if (searchType === 'quick-search') {
      request.calledfrom = 'listing';
      request.startindex = 0;
      request.resultsize = 25;
      request.searchviaboolean = true;
    }

    if (this.filterDataHandlerService.isCalledFromPersonSearch()) {
      request.module = 'personsearch';
    } else {
      delete request.module;
    }


    // Flatten experience arrays
    request.toyearsofexperience = filterData?.yearsofexperienceTo ? [filterData?.yearsofexperienceTo] : [];
    request.fromyearsofexperience = filterData?.yearsofexperienceFrom ? [filterData?.yearsofexperienceFrom] : [];
    request.currenttoyearsofexperience = filterData?.currenttoyearsofexperience ? [filterData?.currenttoyearsofexperience] : [];
    request.currentfromyearsofexperience = filterData?.currentfromyearsofexperience ? [filterData?.currentfromyearsofexperience] : [];

    if (
      !(request?.toyearsofexperience?.length && request?.fromyearsofexperience?.length) &&
      (request?.toyearsofexperience?.length || request?.fromyearsofexperience?.length)
    ) {
      if (!request?.toyearsofexperience?.length) request.toyearsofexperience = [40];
      else if (!request?.fromyearsofexperience?.length) request.fromyearsofexperience = [0];
    }

    if (
      !(request?.currenttoyearsofexperience?.length && request?.currentfromyearsofexperience?.length) &&
      (request?.currenttoyearsofexperience?.length || request?.currentfromyearsofexperience?.length)
    ) {
      if (!request?.currenttoyearsofexperience?.length) request.currenttoyearsofexperience = [40];
      else if (!request?.currentfromyearsofexperience?.length) request.currentfromyearsofexperience = [0];
    }
    request.nlsearchquery = filterData?.nlsearchquery;
    request.linkedinurl = filterData?.linkedinurl;
    request.isUploadedFromResume = filterData?.isUploadedFromResume;
    request.screeningCriteria = filterData?.screeningCriteria
    // Full AI-suggested pool, so it can be restored (e.g. for the Edit Criteria panel) when this search is reopened from history
    request.screeningCriteriaSuggestions = this.filterStore.screeningCriteria();
    request.exclude_persons = filterData?.exclude_persons ? filterData?.exclude_persons : request?.exclude_persons ? request?.exclude_persons : [];
    if(searchType == 'similar-candidate-resume-upload' || searchType == 'similar-candidate-from-linkedinurl') {
      request.similarcandidates_excludefirstname = filterData?.similarcandidates_excludefirstname ? filterData?.similarcandidates_excludefirstname : '';
      request.similarcandidates_excludelastname = filterData?.similarcandidates_excludelastname ? filterData?.similarcandidates_excludelastname : '';
      request.similarcandidates_jobtitles = filterData?.similarcandidates_jobtitles ? filterData?.similarcandidates_jobtitles : [];
      request.similarcandidates_companies = filterData?.similarcandidates_companies ? filterData?.similarcandidates_companies : [];
    }
    request.include_persons = filterData?.include_persons ? filterData?.include_persons : request?.include_persons ? request?.include_persons : [];
    this.applyJdFilters(request,jdFilterData);
      return request;
  }

  // ── Private helpers (copied from aggregation.service.ts, unchanged) ───

  private getStatusMap(filterData: FilterParams) {
    return {
      jobTitleStatus: filterData.jobTitleStatus ? filterData.jobTitleStatus == 'Current or Past' ? 'All': filterData.jobTitleStatus : CallStage.CURRENT.toString(),
      companyStatus: filterData.companyStatus ? filterData.companyStatus == 'Current or Past' ? 'All' : filterData.companyStatus : CallStage.CURRENT.toString(),
      industryStatus: filterData.industryStatus ? filterData.industryStatus == 'Current or Past' ? 'All': filterData.industryStatus : CallStage.CURRENT.toString(),
      yearsOfExperienceType: filterData.yearsofexperience ? filterData.yearsofexperience : 'Total'
    };
  }


applyJdFilters(request, jdFilterData) {
  const redirectSource = this.route.snapshot.queryParams['redirectSource'];
  const applicable = redirectSource === 'talent-insights';
  if(applicable) {
     request.job_description_keys = this.jdDataService.getJdKeys(jdFilterData);
  }

}

  private applyFilters(request: AggregationRequest, filterData: FilterParams, statusMap: { jobTitleStatus: string; companyStatus: string; industryStatus: string; yearsOfExperienceType: string; }) {


    request.matchcategory = filterData?.overallMatchFilters ? _.flatten(filterData?.overallMatchFilters) : [];

    request.booleanquery = this.getBooleanQuery(filterData);

    request.jobtitles = _.uniq(filterData?.jobTitleSelectedList?.filter(jobTitle => !jobTitle.isExcluded).map(j => j.isSuggested && !j.key.includes('.aisearch') ? `${j.key}.aisearch` : j.key));
    request.exclude_jobtitles = this.getExcludeJobtitles(filterData);
    request.jobtitletype = statusMap.jobTitleStatus;

    request.exclude_locations = this.getExcludeLocations(filterData);

    request.pastjobtitles = filterData?.pastJobTitleSelectedList?.filter(l => !l.isExcluded).map(j => j.isSuggested && !j.key.includes('.aisearch') ? `${j.key}.aisearch` : j.key);
    request.exclude_pastjobtitles = this.getExcludePastJobTitles(filterData);

    request.preferredskills = filterData?.skillsSelectedList?.filter(s => (!s.isMandatory && !s.isExcluded)).map(s => s.isSuggested && !s.key.includes('.aisearch') ? `${s.key}.aisearch` : s.key);

    request.companies = filterData?.companySelectedList?.filter(company => company.isExcluded == false).map(j => j.key);
    request.exclude_companies = this.getExcludeCompanies(filterData);
    request.companytype = statusMap.companyStatus;

    request.companysizes = this.getCompanySizes(filterData);

    request.profilehighlights = filterData.profilehighlights;

    request.industries = filterData?.industrySelectedList?.filter(industry => !industry.isExcluded).map(i => i.key);
    request.industrytype = statusMap.industryStatus;
    request.exclude_industries = this.getExcludeIndustries(filterData);
    request.functions = filterData?.functionSelectedList?.filter(l => !l.isExcluded).map(l => l.key);
    request.seniority = filterData?.seniority ? _.flatten(filterData?.seniority) : [];

    // Years of experience
    request.fromyearsofexperience = this.getFromYrsOfExperience(filterData);
    request.toyearsofexperience = this.getToYrsOfExperience(filterData);
    request.experiencetype = statusMap.yearsOfExperienceType;

    request.currentfromyearsofexperience = this.getYrsInCurrentCompanyFrom(filterData);
    request.currenttoyearsofexperience = !!filterData.currenttoyearsofexperience ? [filterData.currenttoyearsofexperience] : [];

    // Gender
    request.genders = this.getGender(filterData);

    // Education
    request.educationaldegrees = filterData?.educationSelectedList?.map(e => e.key);

    // School
    request.schools = filterData?.schoolSelectedList?.map(s => s.key);

    // Education List
    request.schoollocation = filterData?.schoolLocationSelectedList?.filter(l => !l.isExcluded).map(l => l.key);

    // Exclude education location
    request.exclude_schoollocation = this.getExcludSchoolLocation(filterData);


    // Field Of Study
    request.educationalmajors = filterData?.fieldOfStudySelectedList?.map(fs => fs.key);

    // Likely To Move In
    request.likelytomovetype = filterData.candidateslikelytomove;

    // Changed jobs
    request.changedjobs = filterData.changedJobStatus;
    request.changedjobsstartdurationinmonths = filterData?.changedJobStartDuration;
    request.changedjobsenddurationinmonths = filterData?.changedJobEndDuration;

    request.fullnames = filterData.fullNameSelectedList?.length > 0 ? filterData.fullNameSelectedList.map(x => x.key) : [];

    request.distancefromselectedlocations = filterData?.distancefromselectedlocations ? filterData?.distancefromselectedlocations : 100;
    request.distancefromselectedlocationsmeasurementunit = filterData?.distancefromselectedlocationsmeasurementunit ? filterData?.distancefromselectedlocationsmeasurementunit : 'Kilometers';



    request.toyearsofexperience = _.flatten(request?.toyearsofexperience);
    request.fromyearsofexperience = _.flatten(request?.fromyearsofexperience);
    request.currenttoyearsofexperience = _.flatten(request?.currenttoyearsofexperience);
    request.currentfromyearsofexperience = _.flatten(request?.currentfromyearsofexperience);

    if (!(request?.toyearsofexperience?.length && request?.fromyearsofexperience?.length) &&
      (request?.toyearsofexperience?.length || request?.fromyearsofexperience?.length)) {
      if (!request?.toyearsofexperience?.length) {
        request.toyearsofexperience = [40];
      } else if (!request?.fromyearsofexperience?.length) {
        request.fromyearsofexperience = [0];
      }
    }

    if (!(request?.currenttoyearsofexperience?.length && request?.currentfromyearsofexperience?.length) &&
      (request?.currenttoyearsofexperience?.length || request?.currentfromyearsofexperience?.length)) {
      if (!request?.currenttoyearsofexperience?.length) {
        request.currenttoyearsofexperience = [40];
      } else if (!request?.currentfromyearsofexperience?.length) {
        request.currentfromyearsofexperience = [0];
      }
    }

    request.tograduationyear = this.getYearsOfGraduationKeys('tograduationyear', filterData)
    request.fromgraduationyear = this.getYearsOfGraduationKeys('fromgraduationyear', filterData);
    request.exclude_preferredskills = this.getExcludeSkills(filterData);
    request.exclude_functions = this.getExcludeFunctions(filterData)

  }


  private applyMandatoryFilters(request: AggregationRequest, filterData: FilterParams) {
  const redirectSource = this.route.snapshot.queryParams['redirectSource'];
  const applicable = redirectSource === 'talent-insights' || redirectSource === 'company-insights';

  request.mandatoryskills = this.getMandatorySkills(filterData, applicable);
  request.exclude_mandatoryskills = this.getExcludeMandatorySkills(filterData);
  request.mandatoryjobtitles = (filterData?.jobTitleSelectedList?.filter(s => !!s.isMandatory).map(s => s.key) || [])
    .concat(applicable ? (filterData?.mandatoryjobtitles || []) : []);

  request.mandatorylocations          = applicable ? (filterData?.mandatorylocations || [])          : [];
  request.mandatorycompanies          = applicable ? (filterData?.mandatorycompanies || [])           : [];
  request.mandatoryindustries         = applicable ? (filterData?.mandatoryindustries || [])          : [];
  request.mandatoryschools            = applicable ? (filterData?.mandatoryschools || [])             : [];
  request.mandatoryfunctions          = applicable ? (filterData?.mandatoryfunctions || [])           : [];
  request.mandatoryeducationalmajors  = applicable ? (filterData?.mandatoryeducationalmajors || [])   : [];
  request.mandatoryeducationaldegrees = applicable ? (filterData?.mandatoryeducationaldegrees || [])  : [];
  request.mandatoryseniority          = applicable ? (filterData?.mandatoryseniority || [])           : [];
  request.insightsmandatoryskills    = applicable ? (filterData?.insightsmandatoryskills || []) : [];

  if (applicable && filterData?.recentcompanyhires) request.recentcompanyhires = true;
  if (applicable && filterData?.recentcompanyattrition) request.recentcompanyattrition = true;
  if (applicable && filterData?.lastjobcompanyids?.length) request.lastjobcompanyids = filterData.lastjobcompanyids;
  if(applicable && filterData?.continentstobeincluded?.length) request.continentstobeincluded = [];
}

private applyInsightsFilters(request: AggregationRequest, filterData: FilterParams) {
  const redirectSource = this.route.snapshot.queryParams['redirectSource'];
  const applicable = redirectSource === 'talent-insights' || redirectSource === 'company-insights';
  if (!applicable) return;
  console.log('applyInsightsFilters', filterData);
  if(filterData?.companyid) {
    request.insightscompaniesid = [filterData.companyid];
  }

  if(filterData?.alternativecompanynames) {
    request.insightscompanies = filterData.alternativecompanynames;
  }

  if(filterData?.recentcompanyhires) {
    request.recentcompanyhires = true;
    request.lastjobcompanies = filterData.mandatorycompanies;
    request.mandatorycompanies = [];
  }



  if(filterData?.recentgraduatesonly) {
    request.recentgraduatesonly = true;
  }

  if(filterData?.continentstobeincluded?.length) {
    request.continentstobeincluded = filterData.continentstobeincluded;
    request.locations = [];
  }

  if(filterData.winningtalent) {
    request.lastjobcompanies = filterData.winningtalent;
  }



  const insightsData = filterData?.insightsFilterData;
  request.mandatoryskills = request.mandatoryskills.concat(insightsData?.skillsSelectedList?.filter(s => s.isMandatory && !s.isExcluded).map(s => s.key) || []);

  request.insightsjobtitles          = insightsData?.jobTitleSelectedList?.filter(jobTitle => !jobTitle.isExcluded).map(j => j.isSuggested && !j.key.includes('.aisearch') ? `${j.key}.aisearch` : j.key);
  request.insightspastjobtitles      = insightsData?.pastJobTitleSelectedList?.filter(jobTitle => !jobTitle.isExcluded).map(j => j.isSuggested && !j.key.includes('.aisearch') ? `${j.key}.aisearch` : j.key);
  request.insightslocations          = insightsData?.locationSelectedList?.filter(location => !location.isExcluded).map(l => l.key) || [];
  request.insightsindustries         = insightsData?.industrySelectedList?.filter(industry => !industry.isExcluded).map(i => i.key) || [];
  request.insightsschools            = insightsData?.schoolSelectedList?.map(s => s.key) || [];
  request.insightseducationalmajors  = insightsData?.fieldOfStudySelectedList?.map(f => f.key) || [];
  request.insightseducationaldegrees = insightsData?.educationSelectedList?.map(e => e.key) || [];
  request.insightspreferredskills    = insightsData?.skillsSelectedList?.filter(s => !s.isMandatory && !s.isExcluded).map(s => s.key) || [];
  request.insightsfunctions          = insightsData?.functionSelectedList?.filter(f => !f.isExcluded).map(f => f.key) || [];
  request.insightsseniority          = insightsData?.seniority?.length ? [].concat(...insightsData.seniority) : [];
  request.exclude_companies = insightsData?.companySelectedList?.filter(company => company.isExcluded == true).map(j => j.key) || [];
  request.exclude_jobtitles = insightsData?.jobTitleSelectedList?.filter(jobTitle => jobTitle.isExcluded).map(j => j.isSuggested && !j.key.includes('.aisearch') ? `${j.key}.aisearch` : j.key) || [];
  request.exclude_pastjobtitles = insightsData?.pastJobTitleSelectedList?.filter(jobTitle => jobTitle.isExcluded).map(j => j.isSuggested && !j.key.includes('.aisearch') ? `${j.key}.aisearch` : j.key) || [];
  request.exclude_locations = insightsData?.locationSelectedList?.filter(location => location.isExcluded).map(l => l.key) || [];
  request.exclude_industries = insightsData?.industrySelectedList?.filter(industry => industry.isExcluded).map(i => i.key) || [];
  request.exclude_mandatoryskills = insightsData?.skillsSelectedList?.filter(s => s.isMandatory && s.isExcluded).map(s => s.key) || [];
  request.exclude_preferredskills = insightsData?.skillsSelectedList?.filter(s => !s.isMandatory && s.isExcluded).map(s => s.key) || [];
  request.exclude_functions = insightsData?.functionSelectedList?.filter(f => f.isExcluded).map(f => f.key) || [];
  request.booleanquery = insightsData?.booleanquery;

  if (filterData?.companyid) {
    request.insightscompaniesid = [filterData.companyid];
  }
    if(filterData?.recentcompanyattrition) {
    request.recentcompanyattrition = true;
        request.insightscompanies = filterData.mandatorycompanies;
        request.lastjobcompanies = filterData.alternativecompanynames;
    request.mandatorycompanies = [];
    request.insightscompaniesid = [];
  }
}

  // private applyMandatoryFilters(request: AggregationRequest, filterData: FilterParams) {
  //   request.mandatoryskills = this.getMandatorySkills(filterData);
  //   request.exclude_mandatoryskills = this.getExcludeMandatorySkills(filterData);
  //     request.mandatoryjobtitles = this.getMandatoryJobTitle(filterData);
  //       this.manadatoryApplicable(extraInfo) && extraInfo?.redirectData?.mandatoryjobtitles?.length ?
  //         filterData?.jobTitleSelectedList?.filter(s => !!s.isMandatory).map(s => s.key).concat(extraInfo?.redirectData?.mandatoryjobtitles) :
  //         filterData?.jobTitleSelectedList?.filter(s => !!s.isMandatory).map(s => s.key);
  //       request.mandatorylocations = this.manadatoryApplicable(extraInfo) && this.getMandatoryLocations(extraInfo);
  //       request.mandatorycompanies = this.manadatoryApplicable(extraInfo) && this.getMandatoryCompanies(extraInfo);
  //       request.mandatoryindustries = this.manadatoryApplicable(extraInfo) && this.getMandatoryIndustries(extraInfo);
  //       request.mandatoryschools = this.manadatoryApplicable(extraInfo) && this.getMandatorySchools(extraInfo);;
  //       request.mandatoryfunctions = this.manadatoryApplicable(extraInfo) && extraInfo?.redirectData?.mandatoryfunctions?.length ? extraInfo?.redirectData?.mandatoryfunctions : [];
  //       request.mandatoryeducationalmajors = this.manadatoryApplicable(extraInfo)
  //         && this.getMandatoryEducationalMajors(extraInfo);
  //       request.mandatoryeducationaldegrees = this.manadatoryApplicable(extraInfo)
  //         && this.getMandatoryEducationalDegrees(extraInfo);
  //       request.mandatoryseniority = this.manadatoryApplicable(extraInfo)
  //         && extraInfo?.redirectData?.mandatoryseniority?.length ? extraInfo?.redirectData?.mandatoryseniority : [];
  // }


  //  private applyInsightsFilters(request: AggregationRequest, filterData: FilterParams) {
  //     const redirectSource = this.route.snapshot.queryParams['redirectSource'];
  // const applicable = redirectSource === 'talent-insights' || redirectSource === 'company-insights';
  // if (!applicable) return;

  //     request.insightslocations = this.getInsightsLocation(request, filterData);
  //     request.insightscompanies = this.getInsightsCompanies(request, filterData);
  //     request.insightsindustries = this.getInsightsIndustries(request, filterData);
  //     request.insightsschools = this.getInsightsSchools(request, filterData);
  //     request.insightseducationalmajors = this.getInsightsEducationalMajors(request, filterData);
  //     request.insightsjobtitles = this.getInsightsJobTitles(request, filterData);
  //     request.insightspastjobtitles = this.getInsightsPastJobTitles(request, filterData);
  //     request.insightspreferredskills = this.getInsightsPrefferdedSkills(request, filterData);
  //     request.insightsfunctions = this.getInsightsFunctions(request, filterData);
  //     request.insightseducationaldegrees = this.getInsightsEducationalDegrees(request, filterData);
  //     request.insightsseniority = this.getInsightsSeniority(request, filterData);
  //     request.lastjobcompanies = this.getLastJobCompanies(request, filterData);
  //     request.insightscompaniesid = this.getCompanyId(request, filterData);

  //     request.lastjobcompanyids = this.getLastJobCompanyId(request, filterData);
  //     request.durationinmonths = this.getRecentHireAndAttrition(request, filterData);

  //    // if (this.manadatoryApplicable(extraInfo) && extraInfo.redirectData) {
  //       request.recentgraduatesonly = extraInfo.redirectData?.recentgraduatesonly;
  //       request.recentcompanyhires = extraInfo.redirectData?.recentcompanyhires;
  //   //  }

  //     if (extraInfo.redirectSource === 'talent-insights' && extraInfo.redirectData) {
  //       request.recentcompanyattrition = extraInfo.redirectData?.recentcompanyattrition;
  //     }
  //   }



  getYearsOfGraduationKeys(key, filterData: FilterParams) {
    if (key == 'tograduationyear') {
      return filterData.tograduationyear;
    } else if (key == 'fromgraduationyear') {
      return filterData.fromgraduationyear;
    }
    return null
  }

  private getFromYrsOfExperience(filterData: FilterParams) {

    //It should be valid for Zero, Gives falsy value only in case of null
    if (typeof (filterData.yearsofexperienceFrom) == 'number')
      return [filterData.yearsofexperienceFrom];

    return [];
  }

  private getToYrsOfExperience(filterData: FilterParams) {
    if (filterData.yearsofexperienceTo && typeof (filterData.yearsofexperienceTo) == 'number')
      return [filterData.yearsofexperienceTo];

    return [];
  }

  private getYrsInCurrentCompanyFrom(filterData: FilterParams) {

    if (filterData.currentfromyearsofexperience) {
      return [filterData.currentfromyearsofexperience];
    } else {
      return [];
    }
  }

  private getCompanyStatus(filterData: FilterParams) {
    return filterData.companyStatus == CallStage.CURRENT_OR_PAST.toString() ? "All" : filterData.companyStatus;
  }


  // private getMandatoryJobTitle(filterData: FilterParams) {
  //   let manadatoryJobTitles = [];
  //   if (filterData.jobTitleSelectedList?.length) {
  //     manadatoryJobTitles = filterData.jobTitleSelectedList?.filter(el => !!el.isMandatory).map(el => el.key);
  //   }
  //   if (this.manadatoryApplicable() && extraInfo?.redirectData?.mandatoryjobtitles?.length) {
  //     manadatoryJobTitles = manadatoryJobTitles.concat(extraInfo?.redirectData?.mandatoryjobtitles);
  //   }
  //   if ("talent-insights" === extraInfo.redirectSource && redirectDataTI.jobTitleSelectedList?.length) {
  //     manadatoryJobTitles = manadatoryJobTitles.concat(redirectDataTI.jobTitleSelectedList?.filter(el => !!el.isMandatory).map(el => el.key));
  //   }

  //   return manadatoryJobTitles;
  // }

  private getMandatorySkills(filterData: FilterParams, applicable: boolean) {
    let mandatorySkills = [];
    if (filterData.skillsSelectedList?.length) {
      mandatorySkills = filterData.skillsSelectedList?.filter(el => (!!el.isMandatory && !el.isExcluded)).map(el => el.isSuggested && !el.key.includes('.aisearch') ? `${el.key}.aisearch` : el.key);
    }

    if(applicable && filterData?.mandatoryskills?.length) {
      mandatorySkills = mandatorySkills.concat(filterData?.mandatoryskills);
    }

    return mandatorySkills;
  }

  private getMandatoryLocations(extraInfo: ExtraInfo) {
    if (this.isComingFromInsights(extraInfo) && extraInfo?.redirectData?.mandatorylocations?.length)
      return extraInfo?.redirectData?.mandatorylocations;

    return [];
  }

  private getMandatoryCompanies(extraInfo: ExtraInfo) {
    if (this.isComingFromInsights(extraInfo) && extraInfo?.redirectData?.mandatorycompanies?.length)
      return extraInfo?.redirectData?.mandatorycompanies;

    return [];
  }

  private getMandatoryIndustries(extraInfo: ExtraInfo) {
    if (this.isComingFromInsights(extraInfo) && extraInfo?.redirectData?.mandatoryindustries?.length)
      return extraInfo?.redirectData?.mandatoryindustries;

    return [];
  }

  private getMandatorySchools(extraInfo: ExtraInfo) {
    if (this.isComingFromInsights(extraInfo) && extraInfo?.redirectData?.mandatoryschools?.length)
      return extraInfo?.redirectData?.mandatoryschools;

    return [];
  }

  private getMandatoryEducationalMajors(extraInfo: ExtraInfo) {
    if (this.isComingFromInsights(extraInfo) && extraInfo?.redirectData?.mandatoryeducationalmajors?.length)
      return extraInfo?.redirectData?.mandatoryeducationalmajors;

    return [];
  }

  private getMandatoryEducationalDegrees(extraInfo: ExtraInfo) {
    if (this.isComingFromInsights(extraInfo) && extraInfo?.redirectData?.mandatoryeducationaldegrees?.length)
      return extraInfo?.redirectData?.mandatoryeducationalmajors;

    return [];
  }

  private isComingFromInsights(extraInfo: ExtraInfo) {
    return (
      extraInfo?.redirectSource == 'talent-insights' ||
      extraInfo?.redirectSource == 'company-insights'
    );
  }

  // TODO : Use redirectDataCI when working on Company Insights
      private getInsightsLocation(request: AggregationRequest, filterData: FilterParams) {
        if (request?.locations?.length)
      return request?.locations;

    // if (extraInfo?.redirectSource === 'company-insights' && extraInfo?.redirectData?.locations?.length)
    //   return extraInfo?.redirectData?.locations;

    return [];
  }

  private getInsightsCompanies(request: AggregationRequest, filterData: FilterParams) {
     const redirectSource = this.route.snapshot.queryParams['redirectSource'];
  const applicable = redirectSource === 'talent-insights' || redirectSource === 'company-insights';
  if (!applicable) return;
    if (request?.companies?.length)
      return request?.companies;

    if (applicable)
      return request?.companies;



    return [];
  }




  private getInsightsIndustries(request, filter) {
    if ((request?.industries?.length))
      return request?.industries;

    return [];
  }

  private getInsightsSchools(request: AggregationRequest, filter: FilterParams) {
    if ((request?.schools?.length))
      return request?.schools;

    return [];
  }

  private getInsightsEducationalMajors(request: AggregationRequest, filter: FilterParams) {
    if ((request?.educationalmajors?.length))
      return request?.educationalmajors;

    return [];
  }

  private getInsightsJobTitles(request: AggregationRequest, filter: FilterParams) {
    if ((request?.jobtitles?.length))
      return request?.jobtitles;

    return [];
  }

  private getInsightsPastJobTitles(request: AggregationRequest, filter: FilterParams) {
   if(request?.pastjobtitles?.length)
    return request?.pastjobtitles;

    return [];
  }

  private getInsightsPrefferdedSkills(request) {
    if ((request?.preferredskills?.length))
      return request?.preferredskills;
    return [];
  }

  private getInsightsFunctions(request: AggregationRequest, filter: FilterParams) {
    if ((request?.functions?.length))
      return request?.functions;

    return [];
  }

  private getInsightsEducationalDegrees(extraInfo: ExtraInfo, redirectDataTI: FilterParams, redirectDataCI: FilterParams) {
    if ((extraInfo.redirectSource === 'talent-insights' || extraInfo.redirectSource === 'company-insights') && redirectDataTI?.educationSelectedList?.length)
      return redirectDataTI?.educationSelectedList?.map(el => el.key);

    return [];
  }

  private getInsightsSeniority(extraInfo: ExtraInfo, redirectDataTI: FilterParams, redirectDataCI: FilterParams) {
    if ((extraInfo.redirectSource === 'talent-insights' || extraInfo.redirectSource === 'company-insights') && redirectDataTI?.seniority?.length)
      return redirectDataTI?.seniority;

    return [];
  }

  private getLastJobCompanies(extraInfo: ExtraInfo, redirectDataTI: FilterParams, redirectDataCI: FilterParams) {
    if (extraInfo.redirectSource === 'company-insights' && extraInfo?.redirectData?.winningtalent)
      return extraInfo?.redirectData?.winningtalent;

    if (
      extraInfo.redirectSource === 'company-insights' &&
      extraInfo?.redirectData?.losingtalent &&
      extraInfo?.redirectData?.alternative_names
    )
      return extraInfo?.redirectData?.alternative_names?.concat(extraInfo?.redirectData?.name);

    if (
      extraInfo.redirectSource === 'company-insights' &&
      extraInfo?.redirectData?.losingtalent &&
      !extraInfo?.redirectData?.alternative_names
    )
      return [extraInfo.redirectData?.name];

    return [];
  }

  /** Handling company id key which needs to be sent in company insights */
  private getCompanyId(extraInfo: ExtraInfo, redirectDataTI: FilterParams, redirectDataCI: FilterParams) {
    /**In case of losingtalent redirection current company id will send to lastjobcompanyids and insightscompaniesid will be empty for now */
    if (extraInfo?.redirectSource === 'company-insights' && extraInfo.redirectData.losingtalent) {
      return [];
    } else if (extraInfo?.redirectSource === 'company-insights') {
      return extraInfo?.redirectData['alternative_company_id'] || [];
    } else {
      return [];
    }
  }

  private getLastJobCompanyId(extraInfo: ExtraInfo, redirectDataTI: FilterParams, redirectDataCI: FilterParams) {
    if (extraInfo?.redirectSource == 'company-insights' && extraInfo?.redirectData?.recentAttrtion) {
      return extraInfo.redirectData.lastjobcompanyids || [];
    }
    return [];
  }

  /**
   * Get Duration for recent hire and attrition
   * @param extraInfo
   * @returns
   */
  private getRecentHireAndAttrition(extraInfo: ExtraInfo) {
    if (extraInfo?.redirectSource == 'company-insights' && (extraInfo?.redirectData?.recentAttrtion || extraInfo?.redirectData?.recentGrowth)) {
      return extraInfo.redirectData.durationinmonths
    }
    return null
  }

  private getGender(filterData: FilterParams) {
    return filterData?.gender ? [filterData.gender] : ['All'];
  }

  private getExcludeJobtitles(filterData: FilterParams) {
    let excludeJobTitles = filterData.jobTitleSelectedList?.length ?
      filterData.jobTitleSelectedList?.filter(j => !!j.isExcluded).map(j => j.isSuggested && !j.key.includes('.aisearch') ? `${j.key}.aisearch` : j.key) : [];

    return _.uniq(excludeJobTitles);
  }

  private getExcludeLocations(filterData: FilterParams) {
    let excludeLocations = filterData.locationSelectedList?.length ?
      filterData.locationSelectedList?.filter(j => !!j.isExcluded).map(j => j.key) : [];
    return _.uniq(excludeLocations);
  }

  // private getSelectedCompanies(extraInfo: ExtraInfo,redirectDataTI: FilterParams, redirectDataCI: FilterParams, filterData: FilterParams) {
  //   if(this.talentInsightsReqHandlerService.isCompanyInsightsTab)
  // }

  private getExcludeCompanies(filterData: FilterParams) {
    let excludeCompanies = filterData.companySelectedList?.length ?
      filterData.companySelectedList?.filter(j => !!j.isExcluded).map(j => j.key) : [];
    return _.uniq(excludeCompanies);
  }

  private getExcludeIndustries(filterData: FilterParams) {
    let excludeIndustries = filterData.industrySelectedList?.length ?
      filterData.industrySelectedList?.filter(j => !!j.isExcluded).map(j => j.key) : [];
    return _.uniq(excludeIndustries);
  }

  private getExcludSchoolLocation(filterData: FilterParams) {
    let excludeSchoolLocations = filterData.schoolLocationSelectedList?.length ?
      filterData.schoolLocationSelectedList?.filter(j => !!j.isExcluded).map(j => j.key) : [];
    return _.uniq(excludeSchoolLocations);
  }

  private getExcludeSkills(filterData: FilterParams) {
    let excludeSkills = filterData.skillsSelectedList?.length ?
      filterData.skillsSelectedList?.filter(s => (!!s.isExcluded && s.isMandatory == false)).map(s => s.isSuggested && !s.key.includes('.aisearch') ? `${s.key}.aisearch` : s.key) : [];

    return _.uniq(excludeSkills);
  }

  private getExcludeMandatorySkills(filterData: FilterParams) {
    let excludeSkills = filterData.skillsSelectedList?.length ?
      filterData.skillsSelectedList?.filter(s => (!!s.isExcluded && s.isMandatory == true)).map(s => s.isSuggested && !s.key.includes('.aisearch') ? `${s.key}.aisearch` : s.key) : [];

    return _.uniq(excludeSkills);
  }

  private getExcludeFunctions(filterData: FilterParams) {
    let excludeFunctions = filterData.functionSelectedList?.length ?
      filterData.functionSelectedList?.filter(j => !!j.isExcluded).map(j => j.key) : [];
    return _.uniq(excludeFunctions);
  }

  private getCompanySizes(filterData: FilterParams) {
    const companySizeFilterData = filterData.companysize ? _.flatten(filterData.companysize) : [];
    if (companySizeFilterData?.length) {
      return companySizeFilterData;
    }

    return [];
  }

  private getBooleanQuery(filterData: FilterParams) {
    // if ('talent-insights' === extraInfo.redirectSource && redirectDataTI?.keywordSelectedList?.length) {
    //   return redirectDataTI.keywordSelectedList.filter(Boolean).map(el => el.key);
    // }
    // if (this.filterDataHandlerService.isReportDetailsPage && filterData?.keywordSelectedList?.length) {
    //   return filterData.keywordSelectedList.filter(Boolean).map(el => el.key);
    // }
    return filterData.booleanquery ? filterData.booleanquery : "*";
  }

  private getExcludePastJobTitles(filterData: FilterParams) {
    let excludePastJobTitles = filterData?.pastJobTitleSelectedList?.length ?
      filterData.pastJobTitleSelectedList?.filter(j => !!j.isExcluded).map(j => j.isSuggested && !j.key.includes('.aisearch') ? `${j.key}.aisearch` : j.key) : [];
    return _.uniq(excludePastJobTitles);
  }

  manadatoryApplicable() {
    const queryMap = this.route.snapshot.queryParams['redirectSource'];
    return (
     queryMap === 'talent-insights' || queryMap === 'company-insights'
    );
  }


  getInsightsAggregates(statusMap?: StatusMap) {

    if (!statusMap || _.isEmpty(statusMap)) {
      statusMap = this.getStatusMap(this.filterStore.getFilters());
    }
    console.log('getInsightsAggregates: statusMap', statusMap);
    const aggObservations: Observable<any>[] = [
      this.getAggregationReport('location_name-location_continent'),
      this.getAggregationReport('skills'),
      this.getAggregationReport('currentyearsofexperience'),
      this.getAggregationReport('education'),
      this.getAggregationReport('school'),
      this.getAggregationReport('fieldofstudy'),
      this.getAggregationReport('seniority'),
      this.getAggregationReport('gender'),
      this.getAggregationReport('school_location'),
      // this.getAggregationReport('nationality'),
      // this.getAggregationReport('ethnicity')
    ];
    if ((statusMap.jobTitleStatus === CallStage.CURRENT_OR_PAST.toString()) || (statusMap.jobTitleStatus === 'All')) aggObservations.push(this.getAggregationReport('jobtitle'));
    if (statusMap.jobTitleStatus === CallStage.CURRENT.toString()) aggObservations.push(this.getAggregationReport('current_jobtitle'));
    if (statusMap.jobTitleStatus === CallStage.PAST.toString()) aggObservations.push(this.getAggregationReport('pastjobtitle'));

    if ((statusMap.companyStatus === CallStage.CURRENT_OR_PAST.toString()) || (statusMap.companyStatus === 'All')) aggObservations.push(this.getAggregationReport('company'));
    if (statusMap.companyStatus === CallStage.CURRENT.toString()) aggObservations.push(this.getAggregationReport('current_company'));
    if (statusMap.companyStatus === CallStage.PAST.toString()) aggObservations.push(this.getAggregationReport('pastcompany'));

    if ((statusMap.industryStatus === CallStage.CURRENT_OR_PAST.toString()) || (statusMap.industryStatus === 'All')) aggObservations.push(this.getAggregationReport('industry'));
    if (statusMap.industryStatus === CallStage.CURRENT.toString()) aggObservations.push(this.getAggregationReport('current_industry'));
    if (statusMap.industryStatus === CallStage.PAST.toString()) aggObservations.push(this.getAggregationReport('pastindustry'));

    if (statusMap.yearsOfExperienceType === 'Total') aggObservations.push(this.getAggregationReport('totalyearsofexperience'));
    if (statusMap.yearsOfExperienceType === 'Average') aggObservations.push(this.getAggregationReport('averageyearsofexperience'));

    this.loaderEventsService.emitLoaderObject(LoaderType.INSIGHTS);

    aggObservations.forEach(obs => obs.subscribe(res => this.emitInsightsObject(res)));
  }

  private getAggregationReport(aggregatetype: string, obj?: any) {
    // const localRequestObject = this.aggReqObjHandler.getSharedRequestObject();
    const localRequestObject ={ ...this.getHireRequestWithFilters() }
    Object.assign(localRequestObject, {
      resultsize: 1,
      aggregatetype: aggregatetype
    });
    if ((aggregatetype === "changedjobs") && obj && !_.isEmpty(obj)) {
      Object.assign(localRequestObject, {
        // changedjobsstartdurationinmonths: obj?.statrDuration,
        changedjobsenddurationinmonths: obj?.endDuration,
      });
    }
    if ((aggregatetype === "changedjobs")) {
      Object.assign(localRequestObject, {
        // changedjobsstartdurationinmonths: obj?.statrDuration,
        changedjobs: 'yes'
      });
    }

    if (aggregatetype == 'school' || aggregatetype == 'education' || aggregatetype == 'fieldofstudy' || aggregatetype == 'school_location') {
      Object.assign(localRequestObject, {
        considerfilterforaggregates: true
      });
    }
    delete localRequestObject["talentdiscoveryhistoryid"];


    localRequestObject["calledfrom"] = "listing";
    delete localRequestObject["input"];
    delete localRequestObject["typeahead"];

    return this.personSearchService.getTotalAggs(localRequestObject);

  }

    private insightsDataSubject: Subject<any> = new Subject();

    get insightsDataEvent(): Observable<any> {
      return this.insightsDataSubject.asObservable();
    }

    emitInsightsObject(insightsData: any) {
      this.insightsDataSubject.next(insightsData);
    }

    processAggregationResponse(
      pIData: PersonInsightsData,
      resp: any,
      reqObj: AggregationRequest
    ) {
      const response = Object.assign({}, resp);
      const personInsightsData = Object.assign({}, pIData);
      const localReqObj = Object.assign({}, reqObj);
      if(localReqObj.aggregatetype === 'nationality'){
        // personInsightsData.dataSourceNationality =
        // this.fusionChartService.nationalityChart(response?.data?.aggregations?.nationality || [], response?.data?.totalresultscount);
      } else if(localReqObj.aggregatetype === 'ethnicity'){
        // personInsightsData.dataSourceEthnicity =
        // this.fusionChartService.ethnicityChart(response?.data?.aggregations?.ethnicity || [], response?.data?.totalresultscount);
      } else if (localReqObj.aggregatetype === "gender") {
        personInsightsData.dataSourceGender =
          this.createDataSourceGender(response);
      } else if (localReqObj.aggregatetype === "currentyearsofexperience") {
        personInsightsData.dataSourceExpInCurrentRole =
          this.createExpInCurrentRole(response);
      } else if (
        localReqObj.aggregatetype === "totalyearsofexperience" ||
        localReqObj.aggregatetype === "averageyearsofexperience"
      ) {
        personInsightsData.dataSourceCurrentExperience =
          this.createCurrentExperience(response, localReqObj.aggregatetype);
      } else if (localReqObj.aggregatetype === "seniority") {
        personInsightsData.dataSourceSeniority =
          this.createSeniorityData(response);
      } else if (localReqObj.aggregatetype === "location_name-location_continent") {
        personInsightsData.topLocations = response['location_name-location_continent'];
        personInsightsData.dataSourceWorkCountry =
          this.createWorkCountryData(response);
      } else if (
        localReqObj.aggregatetype === "current_company" ||
        localReqObj.aggregatetype === "company" ||
        localReqObj.aggregatetype === "pastcompany"
      ) {
        personInsightsData.dataSourceCurrentCompany = this.createCompanyData(
          !!response[localReqObj.aggregatetype]
            ? response[localReqObj.aggregatetype].slice(0, 5)
            : []
        );
      } else if (
        localReqObj.aggregatetype === "jobtitle" ||
        localReqObj.aggregatetype === "current_jobtitle" ||
        localReqObj.aggregatetype === "pastjobtitle"
      ) {
        personInsightsData.titles = response[localReqObj.aggregatetype]?.slice(0, 5);
      } else if (localReqObj.aggregatetype === "school") {
        personInsightsData.school =
          response[localReqObj.aggregatetype]?.slice(0, 5) || [];
      } else if (localReqObj.aggregatetype === "skills") {
        personInsightsData.skills =
          response[localReqObj.aggregatetype]?.slice(0, 5) || [];
      } else if (localReqObj.aggregatetype === "fieldofstudy") {
        personInsightsData.fieldOfStudy =
          response[localReqObj.aggregatetype]?.slice(0, 5) || [];
      } else if (
        localReqObj.aggregatetype === "current_industry" ||
        localReqObj.aggregatetype === "industry" ||
        localReqObj.aggregatetype === "pastindustry"
      ) {
        personInsightsData.currentIndustry =
          response[localReqObj.aggregatetype]?.slice(0, 5) || [];
      }
      return personInsightsData;
    }

      createDataSourceGender(resp) {
    const response = Object.assign({}, resp);
    let dataSourceGender;
     dataSourceGender = this.fusionChartService.genderDiversityChart(response?.gender, 'personSearchInsights');

    return Object.assign({}, dataSourceGender);
  }

   private createCompanyData(companies: any[]) {
    const CharForCurrentCompany = [];
    companies.forEach((x) => {
      let dataObj = {
        label: `${x.key.charAt(0).toUpperCase()}${x.key.slice(1)}`,
        value: x.doc_count,
        tooltext: `${x.key.charAt(0).toUpperCase()}${x.key.slice(
          1
        )} <br> Talent count: ${x.doc_count}`,
        companylogo: x.companylogo,
        name: x.name,
        industry: x.industry,
        location: x.location,
        size: x.size,
        website: x.website,
        linkedin_url: x.linkedin_url,
        tags: x.tags,
        twitter_url: x.twitter_url,
        github_url: x.github_url,
        facebook_url: x.facebook_url,
      };
      CharForCurrentCompany.push(dataObj);
    });

    const dataSourceCurrentCompany = {
      type: "column2d",
      dataFormat: "json",
      height: 14,
      width: 14,
      chart: {
        renderAt: "chart-container",
        paletteColors: "#5c63a2",
        showPercentValues: "1",
        yAxisNameFontColor: "#2C2C2C",
        legendPosition: "right",
        theme: "fusion",
        showValues: "1",
        labelFont: "Poppins",
        legendItemFont: "Poppins",
        legendItemFontSize: "12",
      },
      data: CharForCurrentCompany,
    };
    return dataSourceCurrentCompany;
  }

  createWorkCountryData(response) {
    const CharForworkCountry = [];
    CharForworkCountry.push(["Location", "Talent count"]);
    response['location_name-location_continent']?.slice(0, 10).forEach((x) => {
      const dataObj = [
        `${x.key.charAt(0).toUpperCase()}${x.key.slice(1)}`,
        x.doc_count,

      ];
      CharForworkCountry.push(dataObj);
    });
    return CharForworkCountry;
  }

  private createSeniorityData(response) {
    const CharForSeniority = [];

    const dataSourceSeniority = this.fusionChartService.seniorityChart(response?.seniority,'personSearchInsights');

    return dataSourceSeniority;
  }

  private createCurrentExperience(response, type) {
    const dataSourceCurrentExperience = this.fusionChartService.yearsOfExperience(response[type], 'personSearchInsights')
    return dataSourceCurrentExperience;
  }

  private createExpInCurrentRole(response: any) {
    const dataSourceExpInCurrentRole = this.fusionChartService.yearsOfExperience(response?.currentyearsofexperience, 'personSearchInsights');
    return dataSourceExpInCurrentRole;
  }

   getStatsAggs() {
        forkJoin([
          // this.getAggregationReport("likelytomove"),
          this.getAggregationReport("total_tenure_median"),
          this.getAggregationReport("gender"),
          this.getAggregationReport("changedjobs")
        ]).subscribe(res => this.emitStatsObject(res));
    }

      emitStatsObject(statsObject: [any, any, any]) {
    this.statsSubject.next(statsObject);
  }

    private statsSubject: Subject<[any, any, any]> = new Subject();



  get statsUpdateEvent(): Observable<[any, any, any]> {
    return this.statsSubject.asObservable();
  }

    /**
   * Observable for handling insights-export request signal
   */
  private hireInsightsRequestObservable: Subject<any> = new Subject();

    /**
     * Send request for insights export
     * @param requestData
     */
    sendHireInsightsExportRequest(requestData: ExportRequest) {
      this.hireInsightsRequestObservable.next(requestData);
    }

      /**
   * Getter for insights-export request observable
   */
  get hireInsightsExportRequestEvent(): Observable<any> {
    return this.hireInsightsRequestObservable.asObservable();
  }

  private hireInsightsExportObservable: Subject<any> = new Subject();
  sendInsightsExportRequest(requestData: ExportPayload) {
    this.hireInsightsExportObservable.next(requestData);
  }

  get insightsExportRequestEvent(): Observable<any> {
    return this.hireInsightsExportObservable.asObservable();
  }
}
